import { useState, useEffect, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
    UserSession,
    AdminUser
} from '../../utils'
import { Client, Account } from "appwrite";
import ThemeOption from '../../components/ThemeOption'
import DashboardMenu from '../../components/DashboardMenu'
import FileUpload from '../../components/FileUpload'
import RunFunction from '../../components/RunFunction'
import Multiselect from '../../components/Multiselect'
import Toggle from '../../components/Toggle'
import Counter from '../../components/Counter'
import InfoTooltip from '../../components/InfoTooltip'
import LogoLoader from '../../components/LogoLoader';
import { storage, functions } from '../../appwrite/config'

import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { Input, Tabs, Tab } from '@mui/material'


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);

var mountedLoad = false;


export default function ScheduleExtractor() {
    const navigate = useNavigate()
    const [userSession, setUserSession] = useAtom(UserSession);
    const [tab, setTab] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(true);

    // const [ignoreFirstColumn, setIgnoreFirstColumn] = useState(true);

    const switchTab = (event, newTab) => {
        setTab(newTab);
    };
    

    useEffect(() => {
        if (!mountedLoad) mountedLoad = true;
    }, []);

    // Set authenticated session on initial page load
    useEffect(() => {
        const checkSession = async () => {
            try {
                const user = await account.get()
                return user
            } catch (error) {
                navigate('/')
            }
        }
        setUserSession(checkSession())
    }, []);

    // Display loading screen while the page loads
    useLayoutEffect(() => {
        const timeoutId = setTimeout(() => {
            setIsLoading(false);
            setTimeout(() => {
                setIsVisible(false);
            }, 750);
        }, 0);
        
        return () => clearTimeout(timeoutId);
    }, []);

    return (<>
        {userSession && <DashboardMenu path="" user={userSession} page={<div className="page-content">
            {/* <div className='theme-options'>
                <ThemeOption theme="dark"/>
                <ThemeOption theme="light"/>
                <ThemeOption theme="purple"/>
            </div> */}
            <Typography variant="h4" gutterBottom>Schedule Extractor</Typography>

            <Tabs
                value={tab}
                onChange={switchTab}
            >
                <Tab icon={<InsertDriveFileOutlinedIcon />} iconPosition="start" label="PDF Scan" />
                <Tab icon={<ImageOutlinedIcon />} iconPosition="start" label="Image Scan" />
            </Tabs>

            <FileUpload multiple type={tab === 0 ? "pdf" : "png jpg jpeg"} refresh={tab}/>

            {/* Advanced settings */}
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    id="panel1-header"
                >
                    <Typography component="span">Advanced Settings <i>(optional)</i></Typography>
                </AccordionSummary>

                {/* PDF settings */}
                {tab === 0 && <AccordionDetails>
                    <Toggle
                        text="Additional cleanup using AI"
                        state={false}
                        widgetName="schedule-extractor"
                        docKey="pdf_gptCleanup"
                        note="(slower)"
                        tooltip="Use AI to clean up any minor formatting issues, such as typos in words, or extra spaces. Takes longer to process per file, so turn this off if you are processing a large batch of files and care more about speed."
                    />
                    {/* <InfoTooltip text="Will be ignored if additional cleanup is used." /> */}
                    <Toggle
                        text="Preserve first column formatting"
                        state={true}
                        widgetName="schedule-extractor"
                        docKey="pdf_ignoreFirstColumn"
                        tooltip="Whether to preserve default text formatting in the first column, which is usually the symbol/key for the plant type. If turned off, text in the first column will be converted to Title Case like the rest of the data."
                    />
                    {/* <Toggle
                        text="Show schedules in process log"
                        state={true}
                        widgetName="schedule-extractor"
                        docKey="pdf_logImages"
                    /> */}
                    
                    <br/>
                    <Multiselect
                        header="Schedule keywords"
                        docKey="pdf_schedKeywords"
                        options={["Schedule", "Legend"]}
                        inputText="New keyword"
                        resetButtonText="Use Default Keywords"
                        widgetName="schedule-extractor"
                        tooltip="Keywords listed here will be used to determine the presence of a schedule (if found alongside header keywords)."
                    />
                    <br/>
                    <Multiselect
                        header="Header keywords"
                        docKey="pdf_headKeywords"
                        options={["qty", "quantity", "symbol", "key"]}
                        inputText="New keyword"
                        resetButtonText="Use Default Keywords"
                        widgetName="schedule-extractor"
                        tooltip="Keywords listed here will be used to determine the presence of a schedule (if found alongside schedule keywords)."
                    />
                    <br/>

                    <Counter
                        number={50}
                        header="Schedule margin"
                        label="Pixels"
                        widgetName="schedule-extractor"
                        docKey="pdf_schedMargin"
                        tooltip="Margin (in pixels) around the schedule header to search for schedule data. Values that are too high may result in non-relevant nearby data being included in the schedule, while values that are too low may result in schedule rows getting missed. Default is 50."
                    />
                    <Counter
                        number={500}
                        header="Minimum schedule box-size"
                        label="Pixels"
                        widgetName="schedule-extractor"
                        docKey="pdf_minSchedSize"
                        tooltip="Minimum size (in pixels) of the schedule box processed by the OCR engine. A box too small may result in poor table sorting. Default is 500x500."
                    />
                </AccordionDetails>}

                {/* Image settings */}
                {tab === 1 && <AccordionDetails>
                    <Toggle
                        text="Additional cleanup using AI"
                        state={true}
                        widgetName="schedule-extractor"
                        docKey="img_gptCleanup"
                        note="(slower)"
                        tooltip="Use AI to clean up any minor formatting issues, such as typos in words, or extra spaces. Takes longer to process per file, so turn this off if you are processing a large batch of files and care more about speed."
                    />
                    <Toggle
                        text="Preserve first column formatting"
                        state={true}
                        widgetName="schedule-extractor"
                        docKey="img_ignoreFirstColumn"
                        tooltip="Whether to preserve default text formatting in the first column, which is usually the symbol/key for the plant type. If turned off, text in the first column will be converted to Title Case like the rest of the data."
                    />
                </AccordionDetails>}
            </Accordion>
            <br/>
            <RunFunction
                name="schedule-extractor"
                path={tab === 0 ? "/pdf" : "/img"}
                button={tab === 0 ? "Process PDF" : "Process Image"}
            />
        </div>} />}

        {isVisible && !mountedLoad && (<div className={`logo-loader ${!isLoading ? 'hidden' : ''}`}>
            <LogoLoader />
        </div>)}
    </>)
}